import { useMemo, useRef } from 'react';
import { useThree, ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import { Line } from '@react-three/drei';
import { useAppStore, type Entity } from '../state/store';
import { useTool } from '../state/store';
import { commitEntityEdit } from '../tools/cad/entityGeometry';
import { constrainDraftPoint, useSnapStore } from '../tools/snapping';
import { GROUND_PLANE } from '../core/scene';

/**
 * Grip editing for selected CAD entities (docs/plans/2d plan Faz 1.1).
 *
 * Classic 2D contract: a selected entity shows blue square grips at
 * its defining points. Dragging a grip live-moves just that point (rubber
 * band feedback); release commits ONE undoable step. Clicking (no drag) an
 * endpoint grip of a line/polyline adds a vertex there — the same click
 * semantics the real product uses; kept minimal and safe.
 *
 * Mounted inside ToolScope so it participates in the normal pointer flow.
 * Only active for the 'select' tool; any running command owns the pointer
 * before this layer (grips are hidden while a command interaction is on).
 */

interface Grip {
    /** stable key within the entity */
    id: string;
    entityId: string;
    /** which data field the grip edits */
    field: 'start' | 'end' | 'center' | 'vertex' | 'radiusPoint' | 'textPosition';
    /** vertex index for polyline/polygon grips */
    index?: number;
    position: [number, number, number];
}

/** Extract the editable grip set of one entity. */
function gripsOf(e: Entity): Grip[] {
    const d = e.data as Record<string, any>;
    const out: Grip[] = [];
    const push = (field: Grip['field'], position: number[] | undefined, index?: number) => {
        if (!Array.isArray(position) || position.length < 2) return;
        out.push({
            id: `${e.id}:${field}:${index ?? ''}`,
            entityId: e.id,
            field,
            index,
            position: [position[0], position[1], position[2] ?? 0] as [number, number, number],
        });
    };
    switch (e.type) {
        case 'line':
            push('start', d.start);
            push('end', d.end);
            break;
        case 'circle':
            push('center', d.center);
            // Radius grip: east rim point (drag changes the radius).
            if (Array.isArray(d.center) && typeof d.radius === 'number') {
                out.push({
                    id: `${e.id}:radius`, entityId: e.id, field: 'radiusPoint', index: -1,
                    position: [d.center[0] + d.radius, d.center[1], d.center[2] ?? 0],
                });
            }
            break;
        case 'rectangle':
            push('start', d.corner1);
            push('end', d.corner2);
            break;
        case 'polyline':
        case 'polygon':
            (Array.isArray(d.vertices) ? d.vertices : []).forEach((v: number[], i: number) => push('vertex', v, i));
            break;
        case 'text':
        case 'mtext':
            push('textPosition', d.position);
            break;
        default:
            break;
    }
    return out;
}

/** Apply a grip drag delta to the entity's data bag (pure). */
function moveGrip(
    data: Record<string, any>,
    grip: Grip,
    target: [number, number, number],
    source: [number, number, number]
): Record<string, any> {
    const d = { ...data };
    switch (grip.field) {
        case 'start': d.start = target; break;
        case 'end': d.end = target; break;
        case 'center': d.center = target; break;
        case 'textPosition': d.position = target; break;
        case 'radiusPoint': {
            const c = d.center as number[];
            if (Array.isArray(c)) d.radius = Math.max(1e-4, Math.hypot(target[0] - c[0], target[1] - c[1]));
            break;
        }
        case 'vertex': {
            const vs = [...(d.vertices as number[][])];
            if (grip.index !== undefined && vs[grip.index]) {
                // delta move keeps the vertex's level; other vertices unchanged
                vs[grip.index] = target;
                d.vertices = vs;
            }
            break;
        }
    }
    void source;
    return d;
}

const GRIP_SIZE = 0.12;

function GripMarker({ grip, onDown }: { grip: Grip; onDown: (g: Grip, e: ThreeEvent<PointerEvent>) => void }) {
    return (
        <group position={grip.position}>
            {/* Invisible larger hit box keeps small grips easy to grab. */}
            <mesh
                onPointerDown={(e) => { e.stopPropagation(); onDown(grip, e); }}
                onPointerMove={(e) => e.stopPropagation()}
                userData={{ isGrip: true }}
            >
                <boxGeometry args={[GRIP_SIZE * 3, GRIP_SIZE * 3, GRIP_SIZE]} />
                <meshBasicMaterial transparent opacity={0} depthWrite={false} />
            </mesh>
            <Line
                points={[
                    [-GRIP_SIZE, -GRIP_SIZE, 0], [GRIP_SIZE, -GRIP_SIZE, 0],
                    [GRIP_SIZE, GRIP_SIZE, 0], [-GRIP_SIZE, GRIP_SIZE, 0],
                    [-GRIP_SIZE, -GRIP_SIZE, 0],
                ]}
                color="#00aaff"
                lineWidth={2}
            />
        </group>
    );
}

export function GripEditLayer() {
    const tool = useTool();
    const selectedIds = useAppStore((s) => s.selectedIds);
    const entities = useAppStore((s) => s.entities);
    const gridSize = useSnapStore((s) => s.gridSize);
    void gridSize;
    const { camera, gl, raycaster, pointer, scene } = useThree();

    // Live-drag preview state kept in a ref + a single dirty counter so
    // pointermove storms re-render only the moving rubber band, not all grips.
    const dragRef = useRef<{ grip: Grip; from: [number, number, number]; cursor: THREE.Vector3 | null } | null>(null);
    const previewRef = useRef<THREE.Group | null>(null);

    const grips = useMemo(() => {
        if (tool !== 'select') return [];
        return selectedIds.flatMap((id) => {
            const e = entities[id];
            if (!e || e.locked || !e.visible) return [];
            return gripsOf(e);
        });
    }, [tool, selectedIds, entities]);

    const groundAt = (): THREE.Vector3 | null => {
        raycaster.setFromCamera(pointer, camera);
        const target = new THREE.Vector3();
        return raycaster.ray.intersectPlane(GROUND_PLANE, target) ? target : null;
    };

    const handleGripDown = (grip: Grip, _e: ThreeEvent<PointerEvent>) => {
        const ent = entities[grip.entityId];
        if (!ent) return;
        // Snapshot the original data bag once: the live preview mutates the
        // store without history, and the final commit restores it first so
        // the undo step records before = original (not preview state).
        const origData = structuredClone(ent.data) as Record<string, any>;
        dragRef.current = { grip, from: [...grip.position] as [number, number, number], cursor: null };
        // Capture-phase window listeners own the drag gesture (the canvas
        // keeps rendering the preview through the store writes below).
        const onMove = (ev: PointerEvent) => {
            const rect = gl.domElement.getBoundingClientRect();
            const ndc = new THREE.Vector2(
                ((ev.clientX - rect.left) / rect.width) * 2 - 1,
                -((ev.clientY - rect.top) / rect.height) * 2 + 1
            );
            raycaster.setFromCamera(ndc, camera);
            const hit = raycaster.ray.intersectPlane(GROUND_PLANE, new THREE.Vector3());
            if (!hit || !dragRef.current) return;
            const snapped = constrainDraftPoint(hit, camera, gl, null);
            dragRef.current.cursor = snapped;
            const group = previewRef.current;
            if (group) {
                const line = group.userData.line as { points: THREE.Vector3[] } | undefined;
                void line;
                // Move the ghost marker by updating the group position.
                group.position.set(snapped.x, snapped.y, snapped.z);
                group.visible = true;
            }
            // Live-preview the entity edit itself (uncommitted).
            const e2 = useAppStore.getState().entities[grip.entityId];
            if (e2) {
                const next = moveGrip(
                    structuredClone(e2.data),
                    grip,
                    [snapped.x, snapped.y, snapped.z],
                    dragRef.current.from
                );
                useAppStore.setState((st) => ({ entities: { ...st.entities, [grip.entityId]: { ...e2, data: next } } }));
            }
        };
        const onUp = (ev: PointerEvent) => {
            window.removeEventListener('pointermove', onMove, true);
            window.removeEventListener('pointerup', onUp, true);
            const drag = dragRef.current;
            dragRef.current = null;
            if (previewRef.current) previewRef.current.visible = false;
            if (!drag) return;
            const rect = gl.domElement.getBoundingClientRect();
            const ndc = new THREE.Vector2(
                ((ev.clientX - rect.left) / rect.width) * 2 - 1,
                -((ev.clientY - rect.top) / rect.height) * 2 + 1
            );
            raycaster.setFromCamera(ndc, camera);
            const hit = raycaster.ray.intersectPlane(GROUND_PLANE, new THREE.Vector3());
            if (!hit) return;
            const target3 = constrainDraftPoint(hit, camera, gl, null);
            const moved = Math.hypot(target3.x - drag.from[0], target3.y - drag.from[1]) > 1e-6;
            if (!moved) {
                // Click without drag: restore the original data (preview may
                // have nudged it) and leave history untouched.
                const cur = useAppStore.getState().entities[grip.entityId];
                if (cur) useAppStore.getState().updateEntity(grip.entityId, { data: origData });
                return;
            }
            // Restore the original bag first, then commit as ONE undoable
            // step so history before/after are exact (preview writes are
            // never captured into history).
            const cur = useAppStore.getState().entities[grip.entityId];
            if (cur) useAppStore.getState().updateEntity(grip.entityId, { data: origData });
            const original = [...drag.from] as [number, number, number];
            const target = [target3.x, target3.y, target3.z] as [number, number, number];
            commitEntityEdit([grip.entityId], (data) => moveGrip(data, { ...grip, position: original }, target, original));
        };
        window.addEventListener('pointermove', onMove, true);
        window.addEventListener('pointerup', onUp, true);
    };

    if (tool !== 'select' || grips.length === 0) return null;

    return (
        <group>
            {grips.map((g) => (
                <GripMarker key={g.id} grip={g} onDown={handleGripDown} />
            ))}
            {/* Ghost cursor marker shown while a grip drag runs. */}
            <group ref={previewRef} visible={false}>
                <mesh>
                    <sphereGeometry args={[0.05, 8, 8]} />
                    <meshBasicMaterial color="#ffff00" />
                </mesh>
            </group>
        </group>
    );
}
